import { Menu } from 'antd';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { SpaceId } from 'api/models';
import { IconShell } from 'components/atoms/icon-shell';
import { Empty } from 'components/organisms/projects/project-sidebar/spaces-menu/styles';
import { useTranslate } from 'hooks/use-translation';
import { EditSpace } from '../edit-space';
import { generateTabMenuKey } from '../menu-group/utils';
import { SpaceMoreActions } from '../more-actions';
import { SpaceTabMenuItem } from '../space-tab-menu-item/space-tab-menu-item';
import { TitleContainer } from '../title-container';
import type { SpaceSubMenuItemtypes } from './space-menu-item.types';
import { useSpaceSubMenuActions } from './use-space-menu-item';

const { SubMenu } = Menu;

export const SpaceSubMenuItem = ({
  projectId,
  space,
  activeSpaceId,
  activeSpaceTabId,
  setOpenKeys,
  ...rest
}: SpaceSubMenuItemtypes) => {
  const { spaceId, spaceTabs = [] } = space;

  const {
    onSpaceDelete,
    onSpaceDocAdd,
    renderIcon,
    setEditing,
    editing,
    onSpaceUpdate,
  } = useSpaceSubMenuActions({
    projectId,
    setOpenKeys,
  });

  const renderEditMode = useCallback(() => {
    return (
      <EditSpace
        space={space}
        onCancel={() => setEditing(false)}
        onChange={onSpaceUpdate}
      />
    );
  }, [space, onSpaceUpdate, setEditing]);
  const translate = useTranslate();

  const renderContent = useCallback(() => {
    if (!spaceTabs.length) {
      return (
        <Empty ellipsis>
          <FormattedMessage id="space.no_document" />
        </Empty>
      );
    }

    if (spaceTabs.length) {
      return spaceTabs.map((spaceTab) => (
        <SpaceTabMenuItem
          key={generateTabMenuKey(spaceId, spaceTab.spaceTabId)}
          spaceTab={spaceTab}
          spaceId={SpaceId(projectId, spaceId)}
          showNewTab={spaceTab.spaceTabId.toString() === 'new'}
          privateSpace={space.private}
        />
      ));
    }

    return null;
  }, [spaceTabs, spaceId, projectId, space.private]);

  return (
    <SubMenu
      key={spaceId.toString()}
      title={
        <TitleContainer
          editMode={renderEditMode()}
          space={space}
          editing={editing}
          tooltipTitle={
            space.private
              ? translate('space.private.document.add')
              : translate('space.shared.document.add')
          }
          handleAdd={() => onSpaceDocAdd(spaceId)}
          testId={`projects.spaceTab.${space.spaceName}.add_button`}
          moreActions={
            <SpaceMoreActions
              projectId={projectId}
              spaceId={spaceId}
              handleDelete={onSpaceDelete}
              handleEdit={() => setEditing((value) => !value)}
              privateSpace={space.private}
            />
          }
        />
      }
      icon={
        <IconShell
          size="small"
          icon={<IconShell size="small" icon={renderIcon(space)} />}
        />
      }
      {...rest}
    >
      {renderContent()}
    </SubMenu>
  );
};
